<template>
  <div
    class="d-flex flex-column align-stretch justify-center"
    id="instruction-detail"
    style="min-height: 500px"
  >
    <div class="d-flex flex-column align-stretch justify-center">
      <template v-if="instruction && validRevisions.length > 0">
        <div v-if="error != null">
          <ErrorAlert :error="error" @clearErrors="clearErrors" class="mb-4 rounded" />
        </div>
        <PostOrderInstructionRevisionDetail
          v-if="instruction"
          :post-order-id="postOrderId"
          :instruction="instruction"
          :revisions="validRevisions"
          v-model:error="error"
        >
          <template #edit-header-action="{ latestInstructionRevision, setSelectedRevision }">
            <!-- Patrol Instructions: More Actions Dropdown -->
            <ConfirmationDialog
              v-if="
                currentUser?.hasPermission('post_orders.change_postorderinstructions') &&
                latestInstructionRevision.isPublished()
              "
              width="auto"
              title="Create new revision"
              v-model="newRevisionPromptDialog"
              v-model:error="newRevisionError"
            >
              <template #activator="{ props: dialogProps }">
                <AlTooltipButton
                  text="Edit Post Order Instruction"
                  color="secondary-lighten-3"
                  variant="flat"
                  position="bottom"
                  size="auto"
                  class="me-2 px-3 align-self-center"
                  style="font-size: 0.875rem; height: 32px"
                  @click="
                    (e: Event) =>
                      handleEditClick(
                        e as MouseEvent,
                        dialogProps,
                        latestInstructionRevision,
                        setSelectedRevision
                      )
                  "
                >
                  Edit Instruction
                </AlTooltipButton>
              </template>

              <template #message>
                This will create a new revision of the post order instruction in the
                <span class="text-high-emphasis font-weight-bold" style="word-break: normal">
                  Draft</span
                >
                state.

                <br />
                <div class="pt-2">This action cannot be undone.</div>
              </template>

              <template #actions>
                <v-spacer></v-spacer>
                <v-btn
                  @click.stop="closeNewRevisionPromptDialog()"
                  color="error"
                  :disabled="isRevisionPending"
                >
                  Cancel
                </v-btn>
                <v-btn
                  variant="flat"
                  @click.stop="createNewRevision(latestInstructionRevision, setSelectedRevision)"
                  color="success"
                  :loading="isRevisionPending"
                >
                  Continue
                </v-btn>
              </template>
            </ConfirmationDialog>

            <MoreOptionsMenuButton
              v-if="
                currentUser?.hasPermission('post_orders.change_postorderinstructions') &&
                (latestInstructionRevision.isDraft() ||
                  instruction?.type === PostOrderInstructionTypeEnum.Patrol)
              "
            >
              <template #button="{ props }">
                <v-btn v-bind="props" class="me-2 my-2" variant="text" size="32" icon>
                  <v-icon size="28" color="high-emphasis" icon="mdi-dots-vertical" />
                </v-btn>
              </template>
              <!-- Edit Detail Option -->
              <ConfirmationDialog
                v-if="
                  currentUser?.hasPermission('post_orders.change_postorderinstructions') &&
                  latestInstructionRevision.isDraft()
                "
                width="auto"
                title="Create new revision"
                v-model="newRevisionPromptDialog"
                v-model:error="newRevisionError"
              >
                <template #activator="{ props: dialogProps }">
                  <v-list-item
                    density="compact"
                    @click="
                      (e: Event) =>
                        handleEditClick(
                          e as MouseEvent,
                          dialogProps,
                          latestInstructionRevision,
                          setSelectedRevision
                        )
                    "
                    title="Edit Detail"
                  >
                  </v-list-item>
                </template>

                <template #message>
                  This will create a new revision of the post order instruction in the
                  <span class="text-high-emphasis font-weight-bold" style="word-break: normal">
                    Draft</span
                  >
                  state.

                  <br />
                  <div class="pt-2">This action cannot be undone.</div>
                </template>

                <template #actions>
                  <v-spacer></v-spacer>
                  <v-btn
                    @click.stop="closeNewRevisionPromptDialog()"
                    color="error"
                    :disabled="isRevisionPending"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    variant="flat"
                    @click.stop="createNewRevision(latestInstructionRevision, setSelectedRevision)"
                    color="success"
                    :loading="isRevisionPending"
                  >
                    Continue
                  </v-btn>
                </template>
              </ConfirmationDialog>

              <!-- Delete Instruction Option -->
              <ConfirmationDialog
                v-if="
                  currentUser?.hasPermission('post_orders.delete_postorderinstructions') &&
                  instruction?.type === PostOrderInstructionTypeEnum.Patrol
                "
                width="auto"
                title="Delete Instruction"
                v-model="deleteDialogOpen"
                v-model:error="deleteError"
              >
                <template #activator="{ props: deleteDialogProps }">
                  <v-list-item
                    density="compact"
                    title="Delete Instruction"
                    class="text-error"
                    :disabled="deleteInProgress"
                    @click="(e: Event) => handleDeleteClick(e as MouseEvent, deleteDialogProps)"
                  >
                  </v-list-item>
                </template>

                <template #message>
                  Are you sure you want to delete
                  <span class="text-medium-high-emphasis font-weight-bold">{{
                    instruction?.form.title
                  }}</span
                  >?
                  <br />
                  <div class="pt-2">This action cannot be undone.</div>
                </template>

                <template #actions>
                  <v-spacer></v-spacer>
                  <v-btn variant="flat" :disabled="deleteInProgress" @click="closeDeleteDialog()">
                    Cancel
                  </v-btn>
                  <v-btn
                    color="error"
                    variant="flat"
                    :loading="deleteInProgress"
                    @click="confirmDeleteInstruction()"
                  >
                    Delete
                  </v-btn>
                </template>
              </ConfirmationDialog>
            </MoreOptionsMenuButton>
          </template>
        </PostOrderInstructionRevisionDetail>
      </template>

      <v-progress-circular
        v-else-if="loading || isRevisionsPending"
        indeterminate
        class="align-self-center"
        color="secondary"
        width="2"
        size="32"
      />

      <NoDataAvailablePlaceholder
        v-else
        class="pt-6"
        width="400"
        :header="noDataHeader"
        header-class="font-weight-medium text-h5"
      >
        <template #description>
          <slot name="no-data-description" />
        </template>

        <template #primaryAction>
          <slot
            name="no-primary-action"
            v-bind="{
              instruction,
              noValidRevisions: validRevisions.length === 0 && !isRevisionsPending
            }"
          />
        </template>
      </NoDataAvailablePlaceholder>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useAuthStore } from '@/stores'
import { storeToRefs } from 'pinia'
import type { ISystemError } from '@/models/error'
import {
  PostOrderInstructionRevision,
  PostOrderInstructionRevisionStateEnum,
  PostOrderInstructionTypeEnum,
  type IPostOrderInstruction,
  type IPostOrderInstructionRevision
} from '@/models/post-order'

import {
  useCreateNewPostOrderInstructionRevision,
  useFetchPostOrderInstructionRevisions,
  useDeletePostOrderInstruction
} from '@/composables/post-order'

import NoDataAvailablePlaceholder from '../common/NoDataAvailablePlaceholder.vue'

import PostOrderInstructionRevisionDetail from '@/components/post-order/PostOrderInstructionRevisionDetail.vue'
import ErrorAlert from '../common/ErrorAlert.vue'
import ConfirmationDialog from '../common/ConfirmationDialog.vue'
import MoreOptionsMenuButton from '../common/MoreOptionsMenuButton.vue'
import AlTooltipButton from '../common/AlTooltipButton.vue'

interface Props {
  instruction: IPostOrderInstruction | null
  postOrderId: number
  loading: boolean
  noDataHeader: string
}

const props = defineProps<Props>()

// TODO MG 2025-05-11: move set instruction state feature to this component to remove unnecessary error model
const error = defineModel<ISystemError | null>('error', {
  default: null
})

interface Emits {
  (e: 'edit-instruction', instruction: IPostOrderInstruction): void
  (e: 'instruction-deleted', instructionId: number): void
}
const emit = defineEmits<Emits>()

const authStore = useAuthStore()
const { getUser: currentUser } = storeToRefs(authStore)

function clearErrors() {
  error.value = null
}

const instructionId = computed(() => props.instruction?.id!)

const {
  revisions,
  isLoading: isRevisionsPending,
  error: revisionsFetchError
} = useFetchPostOrderInstructionRevisions(props.postOrderId, instructionId)
watch(revisionsFetchError, (newError) => {
  if (newError) {
    error.value = newError
  }
})

const validRevisions = computed(() => {
  return (
    revisions.value?.filter(
      (revision) => revision.state !== PostOrderInstructionRevisionStateEnum.Archived
    ) ?? []
  )
})

const {
  isPending: isRevisionPending,
  error: newRevisionError,
  reset: resetRevisionError,
  mutate: createNewPostOrderRevision
} = useCreateNewPostOrderInstructionRevision()

const newRevisionPromptDialog = ref(false)

const closeNewRevisionPromptDialog = () => {
  newRevisionPromptDialog.value = false
}

// Delete functionality
const {
  mutate: deleteInstruction,
  isPending: deleteInProgress,
  error: deleteError,
  reset: clearDeleteError
} = useDeletePostOrderInstruction(computed(() => props.postOrderId))

const deleteDialogOpen = ref(false)

const closeDeleteDialog = () => {
  deleteDialogOpen.value = false
}

function handleDeleteClick(e: MouseEvent, dialogProps: Record<string, any>) {
  e.stopPropagation()
  clearDeleteError()
  return dialogProps.onClick(e)
}

function confirmDeleteInstruction() {
  if (!props.instruction?.id) return

  deleteInstruction(props.instruction.id, {
    onSuccess: () => {
      emit('instruction-deleted', props.instruction!.id!)
      closeDeleteDialog()
    }
  })
}

const createNewRevision = (
  revision: IPostOrderInstructionRevision,
  setSelectedRevision: (revision: IPostOrderInstructionRevision) => void
) => {
  resetRevisionError()

  createNewPostOrderRevision(
    {
      postOrderId: props.postOrderId,
      instructionId: props.instruction!.id!,
      revisionId: revision.id!
    },
    {
      onSuccess: (data) => {
        const newRevision = new PostOrderInstructionRevision(data)
        setSelectedRevision(newRevision)

        closeNewRevisionPromptDialog()
        emit('edit-instruction', props.instruction!)
      }
    }
  )
}

function handleEditClick(
  e: MouseEvent,
  dialogProps: Record<string, any>,
  latestInstructionRevision: IPostOrderInstructionRevision,
  setSelectedRevision: (revision: IPostOrderInstructionRevision) => void
) {
  e.stopPropagation()

  // Edit the existing draft revision
  if (latestInstructionRevision.isDraft()) {
    emit('edit-instruction', props.instruction!)
  } else {
    // Check if there's already a draft revision
    const existingDraftRevision = validRevisions.value.find(
      (revision) => revision.state === PostOrderInstructionRevisionStateEnum.Draft
    )
    // If a draft revision already exists, navigate to it instead of creating a new one
    if (existingDraftRevision) {
      setSelectedRevision(existingDraftRevision)
    }
    // If latest revision is published and no draft exists, show dialog to create new revision
    else if (latestInstructionRevision.isPublished()) {
      dialogProps.onClick(e)
    }
  }
}
</script>
